import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  IonCard,
  IonIcon,
  IonContent,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonItemGroup,
  IonItemDivider,
  IonText,
  IonRow,
  IonCol,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import {
  timeOutline,
  peopleOutline,
  videocamOutline,
  calendarOutline,
} from 'ionicons/icons';
import styles from './TeamAgenda.module.scss';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../../i18n/locale';

export interface TeamEvent {
  id: string;
  title: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  type: 'meeting' | 'shift' | 'event' | 'task';
  attendees?: string[];
  location?: string;
  isVirtual?: boolean;
  meetingLink?: string;
}

export interface TeamAgendaProps {
  selectedDate: Date;
  isCalendarExpanded: boolean;
  events?: TeamEvent[];
  isLoading?: boolean;
  onDateChange?: (date: Date) => void;
}

const TeamAgenda: React.FC<TeamAgendaProps> = ({
  selectedDate,
  isCalendarExpanded,
  events = [],
  isLoading = false,
  onDateChange,
}) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLIonContentElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  function toDate(val: string | Date): Date {
    if (val instanceof Date) return val;
    return new Date(val);
  }

  function getLocalDateString(date: Date): string {
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function parseLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Report the topmost visible day-section back to the parent (TeamCalendar)
  // as the user scrolls, so its own calendar header stays in sync with the
  // agenda's scroll position — mirrors bookingCalendar/Agenda.tsx's scroll-spy.
  const lastDetectedDateRef = useRef<string | null>(null);
  useEffect(() => {
    const ionContent = scrollContainerRef.current;
    if (!ionContent || !onDateChange) return;

    let scrollTimeout: ReturnType<typeof setTimeout>;
    let scrollEl: HTMLElement | null = null;

    const handleUserScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(async () => {
        const container = scrollEl ?? await ionContent.getScrollElement();
        scrollEl = container;
        const containerRect = container.getBoundingClientRect();
        const dayDividers: HTMLElement[] = Array.from(
          container.querySelectorAll('ion-item-divider[data-date]')
        );
        if (dayDividers.length === 0) return;

        let targetDivider: HTMLElement | null = null;
        for (const divider of dayDividers) {
          const rect = divider.getBoundingClientRect();
          if (rect.top <= containerRect.top + 1) {
            targetDivider = divider;
          } else {
            break;
          }
        }
        if (!targetDivider) targetDivider = dayDividers[0];

        const targetDate = targetDivider.getAttribute('data-date');
        if (targetDate && targetDate !== lastDetectedDateRef.current && targetDate !== getLocalDateString(selectedDate)) {
          lastDetectedDateRef.current = targetDate;
          const date = parseLocalDate(targetDate);
          if (!isNaN(date.getTime())) onDateChange(date);
        }
      }, 30);
    };

    ionContent.addEventListener('ionScroll', handleUserScroll);
    return () => {
      ionContent.removeEventListener('ionScroll', handleUserScroll);
      clearTimeout(scrollTimeout);
    };
  }, [onDateChange, selectedDate]);

  const handleRefresh = useCallback(async (event: CustomEvent) => {
    try {
      setIsRefreshing(true);
      // Add your refresh logic here
      await new Promise(resolve => setTimeout(resolve, 1000));
      event.detail.complete();
    } catch {
      event.detail.complete();
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'meeting':
        return peopleOutline;
      case 'shift':
        return timeOutline;
      case 'event':
        return calendarOutline;
      case 'task':
        return timeOutline;
      default:
        return calendarOutline;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'meeting':
        return '#2196f3';
      case 'shift':
        return '#4caf50';
      case 'event':
        return '#ff9800';
      case 'task':
        return '#9c27b0';
      default:
        return '#757575';
    }
  };

  // Show skeleton if loading or refreshing
  const showSkeleton = (isLoading || isRefreshing);

  return (
    <IonCard className={`${styles.agenda} ${isCalendarExpanded ? styles.expanded : ''}`}>
      <IonContent
        ref={scrollContainerRef}
        scrollY={true}
        scrollEvents={true}
        className={styles.agendaContent}
      >
        <IonRefresher
          slot="fixed"
          onIonRefresh={handleRefresh}
          id="team-agenda-refresher"
        >
          <IonRefresherContent
            pullingIcon={null}
            pullingText=""
            refreshingSpinner="bubbles"
            refreshingText=""
          />
        </IonRefresher>

        {showSkeleton ? (
          <IonList className={styles.skeletonOverlay}>
            {Array.from({ length: 6 }, (_, index) => (
              <IonItem key={index} lines="full">
                <IonSkeletonText animated slot="start" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                <IonLabel>
                  <h3><IonSkeletonText animated style={{ width: '70%' }} /></h3>
                  <p><IonSkeletonText animated style={{ width: '50%' }} /></p>
                  <p><IonSkeletonText animated style={{ width: '40%' }} /></p>
                </IonLabel>
                <IonSkeletonText animated slot="end" style={{ width: '60px', height: '20px' }} />
              </IonItem>
            ))}
          </IonList>
        ) : (
          <IonList>
            {(() => {
              const selectedDateStr = getLocalDateString(selectedDate);

              // Group events by date
              const grouped: { [date: string]: TeamEvent[] } = {};

              if (events && events.length > 0) {
                events.forEach((event: TeamEvent) => {
                  const localDate = getLocalDateString(toDate(event.startTime));
                  if (!grouped[localDate]) grouped[localDate] = [];
                  grouped[localDate].push(event);
                });
              }

              // Sort events within each date
              Object.keys(grouped).forEach(date => {
                grouped[date].sort((a, b) => {
                  const aTime = toDate(a.startTime).getTime();
                  const bTime = toDate(b.startTime).getTime();
                  return aTime - bTime;
                });
              });

              const selectedDateHasEvents = grouped[selectedDateStr] && grouped[selectedDateStr].length > 0;

              if (!selectedDateHasEvents && !isLoading) {
                return (
                  <IonItemGroup>
                    <IonItemDivider sticky id={`day-${selectedDateStr}`} data-date={selectedDateStr}>
                      <IonLabel>
                        <IonText className={`${styles.dateText} ${parseLocalDate(selectedDateStr).toDateString() === new Date().toDateString()
                          ? styles.today
                          : styles.selected
                          }`}>
                          {parseLocalDate(selectedDateStr).toDateString() === new Date().toDateString() && 'Today, '}
                          {parseLocalDate(selectedDateStr).toLocaleDateString(intlLocale(), { weekday: 'long' })},
                          {' '}
                          {parseLocalDate(selectedDateStr).toLocaleDateString(intlLocale(), { month: 'short' })}
                          {' '}
                          <IonText>
                            {parseLocalDate(selectedDateStr).toLocaleDateString(intlLocale(), { day: '2-digit' })}
                          </IonText>
                        </IonText>
                      </IonLabel>
                    </IonItemDivider>
                    <IonList>
                      <IonItem lines="none" className={styles.noEventsContainer}>
                        <IonLabel className={styles.noEvents}>
                          <span className={styles.emptyStateIcon}>
                            <IonIcon icon={calendarOutline} />
                          </span>
                          <IonText className={styles.emptyStateTitle}>{t('pages.teamMessaging.TeamAgenda.noEventsScheduled')}</IonText>
                          <IonText className={styles.emptyStateSubtitle}>{t('pages.teamMessaging.TeamAgenda.addAnEventToGetStarted')}</IonText>
                          <IonButton fill="solid" color="primary">{t('pages.teamMessaging.TeamAgenda.addEvent')}
                          </IonButton>
                        </IonLabel>
                      </IonItem>
                    </IonList>
                  </IonItemGroup>
                );
              }

              // Get all dates with events starting from selected date
              const allDates = Object.keys(grouped).sort();
              const renderDates = allDates.filter(date => date >= selectedDateStr);

              return renderDates.map(date => (
                <IonItemGroup key={`group-${date}`}>
                  <IonItemDivider sticky id={`day-${date}`} data-date={date}>
                    <IonLabel>
                      <IonText className={`${styles.dateText} ${parseLocalDate(date).toDateString() === new Date().toDateString()
                        ? styles.today
                        : parseLocalDate(date).toDateString() === selectedDate.toDateString()
                          ? styles.selected
                          : styles.default
                        }`}>
                        {parseLocalDate(date).toDateString() === new Date().toDateString() && 'Today, '}
                        {parseLocalDate(date).toLocaleDateString(intlLocale(), { weekday: 'long' })},
                        {' '}
                        {parseLocalDate(date).toLocaleDateString(intlLocale(), { month: 'short' })}
                        {' '}
                        <IonText>
                          {parseLocalDate(date).toLocaleDateString(intlLocale(), { day: '2-digit' })}
                        </IonText>
                      </IonText>
                    </IonLabel>
                  </IonItemDivider>
                  <IonList>
                    {grouped[date].map((event: TeamEvent, eventIndex: number) => {
                      const eventColor = getEventColor(event.type);

                      return (
                        <IonItem
                          key={`event-${date}-${event.id}-${eventIndex}`}
                          lines="none"
                          button detail={false}
                          className={styles.eventItem}
                        >
                          <IonCol
                            className={styles.timeContainer}
                            size="3"
                            style={{
                              '--event-color': eventColor
                            } as React.CSSProperties}
                          >
                            <IonLabel>
                              <IonText>
                                {event.startTime instanceof Date && !isNaN(event.startTime.getTime()) ? (
                                  <>
                                    <span>{event.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    <br />
                                    <span className={styles.timeDash}>–</span>
                                    <br />
                                    <span>{event.endTime instanceof Date && !isNaN(event.endTime.getTime()) ? event.endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : t('pages.teamMessaging.TeamAgenda.tbd')}</span>
                                  </>
                                ) : t('pages.teamMessaging.TeamAgenda.timeNotSet')}
                              </IonText>
                            </IonLabel>
                          </IonCol>
                          <IonCol className={styles.contentCol}>
                            <IonRow className={styles.titleRow}>
                              <IonIcon icon={getEventIcon(event.type)} className={styles.eventIcon} />
                              <IonText className={styles.eventTitle}>
                                {event.title}
                              </IonText>
                            </IonRow>
                            {event.description && (
                              <IonRow className={styles.descriptionRow}>
                                <IonText>{event.description}</IonText>
                              </IonRow>
                            )}
                            {event.attendees && event.attendees.length > 0 && (
                              <IonRow className={styles.attendeesRow}>
                                <IonIcon icon={peopleOutline} />
                                <IonText>{t('pages.teamMessaging.TeamAgenda.attendees', { length: event.attendees.length })}</IonText>
                              </IonRow>
                            )}
                            {event.isVirtual && (
                              <IonRow className={styles.virtualRow}>
                                <IonIcon icon={videocamOutline} />
                                <IonText>{t('pages.teamMessaging.TeamAgenda.virtualMeeting')}</IonText>
                              </IonRow>
                            )}
                          </IonCol>
                        </IonItem>
                      );
                    })}
                  </IonList>
                </IonItemGroup>
              ));
            })()}
          </IonList>
        )}
      </IonContent>
    </IonCard>
  );
};

export default TeamAgenda;
